import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, ExternalLink, MapPin, Pencil, Plus, Trash2, Video } from 'lucide-react';
import {
  SESSION_FORMATS,
  SESSION_FORMAT_LABELS,
  sessionInputSchema,
  type SessionFormat,
} from '@ready/shared';
import { useApi, type EventDraft, type EventSession, type SessionInputBody } from '@/lib/api';
import { browserTimeZone, formatSessionTime, fromZonedInput, toZonedInput } from '@/lib/dates';
import { Badge, Button, Card, ErrorText, Field, Input, Select, Textarea } from '@/components/ui';

/*
 * An event's sessions as its authors see them: times in the event's own zone,
 * meeting links and check-in codes, with an inline form to add or edit one.
 */

const DURATION_PRESETS = [30, 45, 60, 90, 120];

export const sessionLabel = (s: { title: string | null }, i: number) =>
  s.title ? `Session ${i + 1}: ${s.title}` : `Session ${i + 1}`;

/** "45 min", "1 hr", "1 hr 30 min". */
export function formatDuration(minutes: number) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m} min`;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

export const durationOf = (s: { startsAt: string; endsAt: string }) =>
  Math.round((new Date(s.endsAt).getTime() - new Date(s.startsAt).getTime()) / 60_000);

export function EventSessions({ event }: { event: EventDraft }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['event', event.id] });
    void queryClient.invalidateQueries({ queryKey: ['events'] });
  };
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteEventSession(id),
    onSuccess: refresh,
  });
  const sessions = [...event.sessions].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const locked = event.status === 'cancelled';
  const canAdd = !locked && (event.kind === 'workshop' || sessions.length === 0);
  const published = event.status === 'published';
  const viewerZone = browserTimeZone();

  return (
    <Card
      title={event.kind === 'webinar' ? 'Session' : 'Sessions'}
      action={
        canAdd && editing !== 'new' ? (
          <Button
            variant="secondary"
            className="w-full sm:w-auto"
            onClick={() => setEditing('new')}
          >
            <Plus className="size-4" aria-hidden /> Add session
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Times are in {event.timeZone}. Learners also see them in their own time zone.
        </p>
        {published && (
          <p className="rounded-lg border border-saffron/40 bg-saffron/10 px-4 py-3 text-sm">
            This event is live. Changing a session's time or place emails everyone registered with
            an updated calendar invite.
          </p>
        )}
        {sessions.length === 0 && editing !== 'new' && (
          <p className="text-sm text-muted-foreground">
            {event.kind === 'webinar'
              ? 'Add the date and time of the webinar to publish it.'
              : 'Add at least one session to publish. A workshop can run over several days.'}
          </p>
        )}
        <ol className="space-y-3">
          {sessions.map((s, i) =>
            editing === s.id ? (
              <li key={s.id}>
                <SessionForm
                  event={event}
                  session={s}
                  heading={`Edit ${sessionLabel(s, i).toLowerCase()}`}
                  onDone={() => {
                    setEditing(null);
                    refresh();
                  }}
                  onCancel={() => setEditing(null)}
                />
              </li>
            ) : (
              <li key={s.id} className="rounded-lg border border-border p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 space-y-1">
                    <p className="font-medium">{sessionLabel(s, i)}</p>
                    <p className="text-sm">
                      {formatSessionTime(s.startsAt, s.endsAt, event.timeZone)}
                    </p>
                    {formatSessionTime(s.startsAt, s.endsAt, viewerZone) !==
                      formatSessionTime(s.startsAt, s.endsAt, event.timeZone) && (
                      <p className="text-xs text-muted-foreground">
                        Your time: {formatSessionTime(s.startsAt, s.endsAt, viewerZone)}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Badge>{SESSION_FORMAT_LABELS[s.format]}</Badge>
                      <Badge>
                        <Clock className="mr-1 size-3" aria-hidden />
                        {formatDuration(durationOf(s))}
                      </Badge>
                    </div>
                  </div>
                  {!locked && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        className="px-3"
                        aria-label={`Edit ${sessionLabel(s, i)}`}
                        onClick={() => setEditing(s.id)}
                      >
                        <Pencil className="size-4" aria-hidden /> Edit
                      </Button>
                      <Button
                        variant="ghost"
                        className="px-3"
                        aria-label={`Delete ${sessionLabel(s, i)}`}
                        disabled={remove.isPending}
                        onClick={() =>
                          window.confirm(
                            published
                              ? 'Delete this session? Everyone registered gets an updated calendar invite.'
                              : 'Delete this session?',
                          ) && remove.mutate(s.id)
                        }
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </Button>
                    </div>
                  )}
                </div>
                <SessionPlace session={s} />
                <div className="mt-3 rounded-lg bg-muted px-3 py-2">
                  <p className="text-sm">
                    Check-in code:{' '}
                    <span className="font-mono text-base font-semibold tracking-widest">
                      {s.checkInCode}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Show this during the session; attendees enter it on the event page to check in.
                  </p>
                </div>
              </li>
            ),
          )}
          {editing === 'new' && (
            <li>
              <SessionForm
                event={event}
                heading={`New session${sessions.length ? ` (session ${sessions.length + 1})` : ''}`}
                onDone={() => {
                  setEditing(null);
                  refresh();
                }}
                onCancel={() => setEditing(null)}
              />
            </li>
          )}
        </ol>
        {event.kind === 'webinar' && sessions.length > 0 && (
          <p className="text-xs text-muted-foreground">
            A webinar has one session. Make it a workshop (while it's a draft) for a series.
          </p>
        )}
        <ErrorText error={remove.error} />
      </div>
    </Card>
  );
}

function SessionPlace({ session: s }: { session: EventSession }) {
  if (!s.venue && !s.meetingUrl) return null;
  return (
    <div className="mt-3 space-y-2 text-sm">
      {s.venue && (
        <p className="flex gap-2">
          <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0">
            {s.venue}
            {s.address && <span className="block text-xs text-muted-foreground">{s.address}</span>}
          </span>
        </p>
      )}
      {s.meetingUrl && (
        <p className="flex gap-2">
          <Video className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0">
            <a
              href={s.meetingUrl}
              target="_blank"
              rel="noreferrer"
              className="-my-2 inline-flex items-center gap-1 break-all py-2 text-primary hover:underline"
            >
              {s.meetingUrl}
              <ExternalLink className="size-3 shrink-0" aria-hidden />
            </a>
            {s.meetingDetails && (
              <span className="block whitespace-pre-line text-xs text-muted-foreground">
                {s.meetingDetails}
              </span>
            )}
          </span>
        </p>
      )}
    </div>
  );
}

/** Tomorrow at 10:00 in the event's zone, for a new session. */
function defaultStart(timeZone: string) {
  const tomorrow = toZonedInput(new Date(Date.now() + 24 * 3600_000), timeZone);
  return `${tomorrow.split('T')[0]}T10:00`;
}

type FieldErrors = Partial<Record<string, string>>;

function SessionForm({
  event,
  session,
  heading,
  onDone,
  onCancel,
}: {
  event: EventDraft;
  session?: EventSession;
  heading: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const api = useApi();
  const tz = event.timeZone;
  const initialDuration = session ? durationOf(session) : 60;
  const [title, setTitle] = useState(session?.title ?? '');
  const [local, setLocal] = useState(
    session ? toZonedInput(session.startsAt, tz) : defaultStart(tz),
  );
  const [duration, setDuration] = useState(String(initialDuration));
  const [custom, setCustom] = useState(!DURATION_PRESETS.includes(initialDuration));
  const [format, setFormat] = useState<SessionFormat>(session?.format ?? 'online');
  const [venue, setVenue] = useState(session?.venue ?? '');
  const [address, setAddress] = useState(session?.address ?? '');
  const [meetingUrl, setMeetingUrl] = useState(session?.meetingUrl ?? '');
  const [meetingDetails, setMeetingDetails] = useState(session?.meetingDetails ?? '');
  const [errors, setErrors] = useState<FieldErrors>({});
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    formRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, []);

  const save = useMutation({
    mutationFn: (body: SessionInputBody) =>
      session ? api.updateEventSession(session.id, body) : api.addEventSession(event.id, body),
    onSuccess: onDone,
  });

  const onSubmit = () => {
    if (!local) {
      setErrors({ startsAt: 'Pick the date and start time' });
      return;
    }
    const inPerson = format !== 'online';
    const online = format !== 'in_person';
    const body: SessionInputBody = {
      title: title.trim() || undefined,
      startsAt: fromZonedInput(local, tz),
      durationMinutes: Number(duration),
      format,
      venue: inPerson ? venue.trim() || undefined : undefined,
      address: inPerson ? address.trim() || undefined : undefined,
      meetingUrl: online ? meetingUrl.trim() || undefined : undefined,
      meetingDetails: online ? meetingDetails.trim() || undefined : undefined,
    };
    const parsed = sessionInputSchema.safeParse(body);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? 'form');
        next[key] ??=
          key === 'durationMinutes' ? 'Use between 5 minutes and 24 hours' : issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    save.mutate(body);
  };

  const id = session?.id ?? 'new';
  const err = (k: string) =>
    errors[k] ? <p className="text-xs text-destructive">{errors[k]}</p> : null;

  return (
    <form
      ref={formRef}
      className="space-y-4 rounded-lg border border-primary/40 p-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <h3 className="font-medium">{heading}</h3>
      <div className="max-w-3xl space-y-4">
        <Field label="Title (optional)" htmlFor={`s-title-${id}`}>
          <Input
            id={`s-title-${id}`}
            maxLength={200}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={event.kind === 'workshop' ? 'Day 1: Spotting phishing' : ''}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Date and start time" htmlFor={`s-start-${id}`} hint={`Times are in ${tz}.`}>
            <Input
              id={`s-start-${id}`}
              type="datetime-local"
              required
              value={local}
              onChange={(e) => setLocal(e.target.value)}
            />
            {err('startsAt')}
          </Field>
          <Field label="Duration" htmlFor={`s-duration-${id}`}>
            <Select
              id={`s-duration-${id}`}
              value={custom ? 'custom' : duration}
              onChange={(e) => {
                if (e.target.value === 'custom') setCustom(true);
                else {
                  setCustom(false);
                  setDuration(e.target.value);
                }
              }}
            >
              {DURATION_PRESETS.map((m) => (
                <option key={m} value={m}>
                  {formatDuration(m)}
                </option>
              ))}
              <option value="custom">Custom…</option>
            </Select>
            {custom && (
              <Input
                aria-label="Duration in minutes"
                type="number"
                min={5}
                max={1440}
                inputMode="numeric"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="Minutes"
              />
            )}
            {err('durationMinutes')}
          </Field>
        </div>
        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium">Where</legend>
          <div className="grid gap-2 sm:grid-cols-3">
            {SESSION_FORMATS.map((f) => (
              <label
                key={f}
                className={`flex min-h-10 cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${format === f ? 'border-primary bg-primary/5' : 'border-border'}`}
              >
                <input
                  type="radio"
                  name={`s-format-${id}`}
                  className="size-4 accent-primary"
                  checked={format === f}
                  onChange={() => setFormat(f)}
                />
                {f === 'hybrid' ? 'Hybrid' : SESSION_FORMAT_LABELS[f]}
              </label>
            ))}
          </div>
        </fieldset>
        {format !== 'online' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Venue" htmlFor={`s-venue-${id}`}>
              <Input
                id={`s-venue-${id}`}
                maxLength={300}
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                placeholder="Seminar Hall 2, Main Building"
              />
              {err('venue')}
            </Field>
            <Field label="Address or directions (optional)" htmlFor={`s-address-${id}`}>
              <Input
                id={`s-address-${id}`}
                maxLength={500}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
              {err('address')}
            </Field>
          </div>
        )}
        {format !== 'in_person' && (
          <>
            <Field
              label="Meeting link"
              htmlFor={`s-url-${id}`}
              hint="Zoom, Google Meet, Teams or any https:// link. Only registered attendees see the link."
            >
              <Input
                id={`s-url-${id}`}
                type="url"
                inputMode="url"
                maxLength={1000}
                value={meetingUrl}
                onChange={(e) => setMeetingUrl(e.target.value)}
                placeholder="https://"
              />
              {err('meetingUrl')}
            </Field>
            <Field label="Meeting details (optional)" htmlFor={`s-details-${id}`}>
              <Textarea
                id={`s-details-${id}`}
                rows={2}
                maxLength={1000}
                value={meetingDetails}
                onChange={(e) => setMeetingDetails(e.target.value)}
                placeholder="Passcode, dial-in numbers"
              />
              {err('meetingDetails')}
            </Field>
          </>
        )}
        {err('title')}
        {err('form')}
      </div>
      {event.status === 'published' && session && (
        <p className="text-xs text-muted-foreground">
          Saving a new time or place emails everyone registered with an updated calendar invite.
        </p>
      )}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button type="submit" className="w-full sm:w-auto" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : session ? 'Save session' : 'Add session'}
        </Button>
        <Button type="button" variant="ghost" className="w-full sm:w-auto" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <ErrorText error={save.error} />
    </form>
  );
}
